/**
 * Authentication Provider Framework — SPA configuration service (v4).
 *
 * Preferred source: GET /api/v1/auth/config (fallback: userApi/auth/config).
 * Production fallback: configData.social_logins.GOOGLE_CLIENT_ID from settings JSON
 * when the Authentication Framework endpoint is not yet deployed.
 */

import configuration from "react-global-configuration";
import { runtimeConfig } from "../config/runtimeConfig";

const CACHE_KEY = "auth_providers_config_v4";
const CACHE_META_KEY = "auth_providers_config_v4_meta";
const DEFAULT_TTL_MS = 30 * 60 * 1000; // 30 minutes

/** @type {object|null} */
let memoryCache = null;

/**
 * @returns {number}
 */
function cacheTtlMs() {
  const fromEnv = process.env.REACT_APP_AUTH_CONFIG_TTL_MS;
  if (fromEnv && !isNaN(Number(fromEnv))) {
    return Number(fromEnv);
  }
  return DEFAULT_TTL_MS;
}

/**
 * @returns {{ data: object, fetchedAt: number }|null}
 */
function readPersistedCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    const metaRaw = sessionStorage.getItem(CACHE_META_KEY);
    if (!raw || !metaRaw) {
      return null;
    }
    const meta = JSON.parse(metaRaw);
    if (!meta || !meta.fetchedAt) {
      return null;
    }
    if (Date.now() - meta.fetchedAt > cacheTtlMs()) {
      return null;
    }
    return { data: JSON.parse(raw), fetchedAt: meta.fetchedAt };
  } catch (e) {
    return null;
  }
}

/**
 * @param {object} data
 */
function writePersistedCache(data) {
  try {
    const fetchedAt = Date.now();
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
    sessionStorage.setItem(
      CACHE_META_KEY,
      JSON.stringify({ fetchedAt: fetchedAt })
    );
    memoryCache = { data: data, fetchedAt: fetchedAt };
  } catch (e) {
    memoryCache = { data: data, fetchedAt: Date.now() };
  }
}

/**
 * Clear memory + session cache (call on logout / forced refresh).
 */
export function invalidateAuthConfigCache() {
  memoryCache = null;
  try {
    sessionStorage.removeItem(CACHE_KEY);
    sessionStorage.removeItem(CACHE_META_KEY);
  } catch (e) {
    // ignore
  }
}

/**
 * @param {object} provider
 * @returns {boolean}
 */
export function isProviderRenderable(provider) {
  if (!provider) {
    return false;
  }
  if (!provider.enabled || !provider.visible) {
    return false;
  }
  if (provider.ready === false) {
    return false;
  }
  return true;
}

/**
 * Legacy production client ID from settings / social_logins.
 * @returns {string}
 */
export function getLegacyGoogleClientId() {
  try {
    const id = configuration.get("configData.social_logins.GOOGLE_CLIENT_ID");
    if (id === undefined || id === null) {
      return "";
    }
    return String(id).trim();
  } catch (e) {
    return "";
  }
}

/**
 * @param {object} [options]
 * @param {boolean} [options.force]
 * @returns {Promise<object>}
 */
export async function loadAuthConfig(options) {
  const force = options && options.force;
  if (!force) {
    if (memoryCache && Date.now() - memoryCache.fetchedAt <= cacheTtlMs()) {
      return memoryCache.data;
    }
    const persisted = readPersistedCache();
    if (persisted) {
      memoryCache = persisted;
      return persisted.data;
    }
  }

  const urls = [
    runtimeConfig.authConfigUrl,
    runtimeConfig.authConfigFallbackUrl,
  ].filter(Boolean);

  let lastError = null;
  for (let i = 0; i < urls.length; i++) {
    try {
      const response = await fetch(urls[i], {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "omit",
      });
      if (!response.ok) {
        lastError = new Error("Auth config HTTP " + response.status);
        continue;
      }
      const json = await response.json();
      const normalized = normalizeAuthConfig(json);
      // Prefer framework only when it actually returns providers.
      if (normalized.providers && normalized.providers.length > 0) {
        writePersistedCache(normalized);
        return normalized;
      }
      lastError = new Error("Auth config returned empty providers");
    } catch (e) {
      lastError = e;
    }
  }

  // Framework unavailable — keep empty catalog; GIS uses legacy Settings ID.
  const empty = normalizeAuthConfig({
    providers: [],
    source: "legacy_settings_fallback",
    version: 4,
  });
  writePersistedCache(empty);
  if (lastError && typeof console !== "undefined" && console.warn) {
    console.warn(
      "[authConfig] /api/v1/auth/config unavailable; using social_logins.GOOGLE_CLIENT_ID when present.",
      lastError
    );
  }
  return empty;
}

/**
 * @param {object} json
 * @returns {{ providers: object[], source: string, version: number }}
 */
function normalizeAuthConfig(json) {
  const providers = Array.isArray(json && json.providers)
    ? json.providers
    : Array.isArray(json)
      ? json
      : [];

  return {
    providers: providers.slice().sort(function (a, b) {
      return (a.sort_order || 0) - (b.sort_order || 0);
    }),
    source: (json && json.source) || "authentication_framework",
    version: (json && json.version) || 4,
  };
}

/**
 * Synchronous read of last loaded config (may be empty).
 * @returns {{ providers: object[], source: string, version: number }}
 */
export function getAuthConfig() {
  if (memoryCache && memoryCache.data) {
    return memoryCache.data;
  }
  const persisted = readPersistedCache();
  if (persisted) {
    memoryCache = persisted;
    return persisted.data;
  }
  return { providers: [], source: "none", version: 4 };
}

/**
 * @returns {object[]}
 */
export function getAuthProviders() {
  return getAuthConfig().providers || [];
}

/**
 * @param {string} key
 * @returns {object|null}
 */
export function getAuthProvider(key) {
  const list = getAuthProviders();
  for (let i = 0; i < list.length; i++) {
    if (list[i].provider === key) {
      return list[i];
    }
  }
  return null;
}

/**
 * Google GIS client ID.
 * 1) Authentication Framework public_client_id when ready+visible
 * 2) Legacy settings only when framework catalog is unavailable
 * @returns {string}
 */
export function getGooglePublicClientId() {
  const config = getAuthConfig();
  const google = getAuthProvider("google");
  const frameworkLoaded =
    config &&
    config.source &&
    config.source !== "none" &&
    config.source !== "legacy_settings_fallback";

  if (frameworkLoaded) {
    // Respect admin visibility/enabled/ready flags from the framework.
    if (!isProviderRenderable(google)) {
      return "";
    }
    const frameworkId = (google.public_client_id || "").trim();
    if (frameworkId) {
      return frameworkId;
    }
    return "";
  }

  if (google && isProviderRenderable(google)) {
    const frameworkId = (google.public_client_id || "").trim();
    if (frameworkId) {
      return frameworkId;
    }
  }
  return getLegacyGoogleClientId();
}

/**
 * Facebook app ID when the provider is allowed to render.
 * Prefer Authentication Framework public_client_id; fall back to legacy settings.
 * @returns {string}
 */
export function getFacebookAppId() {
  const config = getAuthConfig();
  const facebook = getAuthProvider("facebook");
  const frameworkLoaded =
    config &&
    config.source &&
    config.source !== "none" &&
    config.source !== "legacy_settings_fallback";

  let legacyId = "";
  try {
    const raw = configuration.get("configData.social_logins.FB_CLIENT_ID");
    legacyId = raw === undefined || raw === null ? "" : String(raw).trim();
  } catch (e) {
    legacyId = "";
  }

  if (frameworkLoaded) {
    if (!isProviderRenderable(facebook)) {
      return "";
    }
    const frameworkId = (facebook.public_client_id || "").trim();
    if (frameworkId) {
      return frameworkId;
    }
    return legacyId;
  }

  if (facebook && isProviderRenderable(facebook)) {
    const frameworkId = (facebook.public_client_id || "").trim();
    if (frameworkId) {
      return frameworkId;
    }
  }
  return legacyId;
}

/**
 * Providers suitable for social button rendering (excludes email).
 * @returns {object[]}
 */
export function getSocialAuthProviders() {
  return getAuthProviders().filter(function (p) {
    return p.provider !== "email" && isProviderRenderable(p);
  });
}

/**
 * @returns {boolean}
 */
export function isEmailAuthEnabled() {
  const email = getAuthProvider("email");
  if (!email) {
    // If catalog omitted email, keep classic email form available.
    return true;
  }
  return isProviderRenderable(email);
}

export default {
  loadAuthConfig: loadAuthConfig,
  invalidateAuthConfigCache: invalidateAuthConfigCache,
  getAuthConfig: getAuthConfig,
  getAuthProviders: getAuthProviders,
  getAuthProvider: getAuthProvider,
  getGooglePublicClientId: getGooglePublicClientId,
  getFacebookAppId: getFacebookAppId,
  getLegacyGoogleClientId: getLegacyGoogleClientId,
  getSocialAuthProviders: getSocialAuthProviders,
  isEmailAuthEnabled: isEmailAuthEnabled,
  isProviderRenderable: isProviderRenderable,
};
